import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Rocket, Loader2, Sparkles, Check, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const initials = (name) =>
  ((name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?");

// Community shout-out for the people funding a BLOQ's perks. Reads `supporters` off the
// boosts payload and self-hides until the backend surfaces it (see HANDOFF_top_supporters.md).
function TopSupporters({ supporters }) {
  const list = Array.isArray(supporters) ? supporters.filter(Boolean).slice(0, 5) : [];
  if (list.length === 0) return null;
  const rankTint = (i) => (i === 0 ? "bg-amber-400/20 text-amber-300" : i === 1 ? "bg-slate-300/20 text-slate-200" : i === 2 ? "bg-orange-400/20 text-orange-300" : "bg-white/8 text-bloq-muted");
  const label = (s) => {
    if (s.months) return `${s.months} mo`;
    if (s.boosts_count) return `${s.boosts_count}×`;
    return "Booster";
  };
  return (
    <div className="mt-3 rounded-2xl border border-white/8 bg-bloq-s2 p-3.5" data-testid="bloq-top-supporters">
      <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-bloq-muted">
        <Heart className="h-3.5 w-3.5 text-pink-400" /> Top supporters
      </div>
      <div className="space-y-1.5">
        {list.map((s, i) => {
          const u = s.user || s;
          const name = u.display_name || (u.handle ? `@${u.handle}` : "Someone");
          return (
            <div key={u.user_id || u.id || u.handle || i} className="flex items-center gap-2.5" data-testid={`supporter-row-${i}`}>
              <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold", rankTint(i))}>{i + 1}</span>
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-bloq-purple/20 text-[10px] font-bold text-bloq-purple">{initials(u.display_name || u.handle)}</span>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-white">{name}</div>
                {u.handle && u.display_name && <div className="truncate text-[11px] text-bloq-muted">@{u.handle}</div>}
              </div>
              <span className="shrink-0 rounded-full border border-bloq-purple/30 bg-bloq-purple/10 px-2 py-0.5 text-[10px] font-bold text-bloq-purple">{label(s)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const boostErr = (e) => {
  const detail = e?.response?.data?.detail;
  const code = typeof detail === "object" ? detail?.error : null;
  const st = e?.response?.status;
  if (code === "already_boosting") return "You're already boosting this BLOQ.";
  if (code === "payouts_not_setup") return "This BLOQ owner hasn't set up payouts yet.";
  if (st === 400) return "BLOQ owners can't boost their own BLOQ.";
  if (st === 403) return "Join the BLOQ before boosting it.";
  if (st === 404 || st === 405) return "BLOQ Boosts aren't live yet.";
  return (typeof detail === "object" ? detail?.message : detail) || "Couldn't boost this BLOQ.";
};

function BoostDialog({ open, onOpenChange, bloq, plans }) {
  const [plan, setPlan] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const monthly = plans?.monthly?.amount ?? 499;
  const annual = plans?.annual?.amount ?? 4999;
  const boost = async () => {
    setBusy(true);
    try {
      const res = await api.bloqBoostCheckout(bloq.id, plan, `${window.location.origin}/app/bloqs`);
      if (res?.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.error("Couldn't start the boost checkout.");
    } catch (e) {
      toast.error(boostErr(e));
    } finally { setBusy(false); }
  };
  const options = [
    { id: "monthly", label: "Monthly", amount: money(monthly), sub: "per month · cancel anytime" },
    { id: "annual", label: "Annual", amount: money(annual), sub: "per year · save ~2 months" },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-white/10 bg-bloq-s1 text-white sm:max-w-sm" data-testid="boost-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Rocket className="h-5 w-5 text-bloq-purple" /> Boost {bloq?.name}</DialogTitle>
          <DialogDescription className="text-sm text-bloq-muted">Unlock perks for the whole community — bigger uploads, a vanity URL, custom branding and a boosted badge.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {options.map((p) => (
            <button key={p.id} type="button" onClick={() => setPlan(p.id)}
              className={cn("rounded-xl border px-3 py-3 text-left transition-colors", plan === p.id ? "border-bloq-purple/60 bg-bloq-purple/10" : "border-white/10 hover:border-white/20")}
              data-testid={`boost-plan-${p.id}`}>
              <div className="text-sm font-bold">{p.label}</div>
              <div className="text-lg font-bold text-bloq-purple">{p.amount}</div>
              <div className="text-[11px] text-bloq-muted">{p.sub}</div>
            </button>
          ))}
        </div>
        <Button onClick={boost} disabled={busy} className="h-12 w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90" data-testid="boost-confirm-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Rocket className="mr-1.5 h-4 w-4" /> Boost this BLOQ</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function BloqBoostSection({ bloq }) {
  const [open, setOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const { data, refetch } = useQuery({ queryKey: ["bloq-boosts", bloq?.id], queryFn: () => api.bloqBoosts(bloq.id), enabled: !!bloq?.id, retry: false });
  const level = data?.level ?? 0;
  const count = data?.boosts_count ?? 0;
  const nextAt = data?.next_level_at;
  const perks = data?.perks_unlocked || [];
  const myBoost = data?.my_boost;
  const pct = nextAt ? Math.min(100, Math.round((count / nextAt) * 100)) : 0;

  const cancel = async () => {
    if (!window.confirm("Cancel your boost at the end of the current billing period?")) return;
    setCancelling(true);
    try {
      const res = await api.bloqBoostCancel(bloq.id);
      toast.success(res?.active_until ? `Boost will end ${new Date(res.active_until).toLocaleDateString()}` : "Boost canceled");
      await refetch();
    } catch (e) {
      toast.error(boostErr(e));
    } finally { setCancelling(false); }
  };

  return (
    <div className="mt-6" data-testid="bloq-boost-section">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
        <Rocket className="h-3.5 w-3.5 text-bloq-purple" /> Boosts
      </div>
      <div className="rounded-2xl border border-bloq-purple/20 bg-bloq-purple/[0.05] p-4">
        {data ? (
          <>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-white">Level {level}{myBoost?.active ? " · you're boosting 🚀" : ""}</div>
              <div className="text-xs text-bloq-muted">{count}{nextAt ? ` / ${nextAt}` : ""} boosts</div>
            </div>
            {nextAt && (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/8">
                <div className="h-full rounded-full bg-bloq-purple transition-all" style={{ width: `${pct}%` }} data-testid="boost-progress" />
              </div>
            )}
            {perks.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {perks.map((p) => (
                  <span key={p} className="inline-flex items-center gap-1 rounded-full border border-bloq-purple/30 bg-bloq-purple/10 px-2 py-0.5 text-[10px] font-semibold text-bloq-purple">
                    <Check className="h-2.5 w-2.5" /> {p}
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-white/80"><Sparkles className="h-4 w-4 text-bloq-purple" /> Boost this BLOQ to unlock community perks.</div>
        )}
        {myBoost?.active ? (
          <div className="mt-3 space-y-1.5">
            <div className="text-xs text-bloq-muted" data-testid="boost-my-status">
              {myBoost.cancel_at_period_end ? "Ends" : "Renews"} {myBoost.renews_at ? new Date(myBoost.renews_at).toLocaleDateString() : "soon"}
              {myBoost.plan ? ` · ${myBoost.plan}` : ""}
            </div>
            {!myBoost.cancel_at_period_end && (
              <Button onClick={cancel} disabled={cancelling} variant="outline" className="w-full rounded-full border-white/15 bg-transparent text-white hover:bg-white/5" data-testid="boost-cancel-btn">
                {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel boost"}
              </Button>
            )}
          </div>
        ) : (
          <Button onClick={() => setOpen(true)} className="mt-3 w-full rounded-full bg-bloq-purple font-bold text-white hover:bg-bloq-purple/90" data-testid="bloq-boost-btn">
            <Rocket className="mr-1.5 h-4 w-4" /> Boost this BLOQ
          </Button>
        )}
      </div>
      <TopSupporters supporters={data?.supporters} />
      <BoostDialog open={open} onOpenChange={setOpen} bloq={bloq} plans={data?.plans} />
    </div>
  );
}
